// Light mode colors - Based on Broker24 logo
export const lightColors = {
  // Brand colors - Primary Blue from logo
  brand: {
    50: '#e8f4fd',
    100: '#c1e1fa',
    200: '#99cef7',
    300: '#72bbf4',
    400: '#4ba8f1',
    500: '#4A90E2', // Primary from logo
    600: '#3b73b5',
    700: '#2d5688',
    800: '#1e395b',
    900: '#0f1c2e',
  },

  // Secondary colors - Cyan/Teal from logo
  secondary: {
    50: '#e6faf5',
    100: '#b3f0e1',
    200: '#80e6cd',
    300: '#4ddcb9',
    400: '#20d2a5',
    500: '#20C997', // Teal/Cyan from logo
    600: '#1aa179',
    700: '#13795b',
    800: '#0d513d',
    900: '#06281f',
  },

  // Accent colors - Dark gray from logo
  accent: {
    50: '#f2f4f5',
    100: '#d9dfe2',
    200: '#c0cacf',
    300: '#a7b5bc',
    400: '#8ea0a9',
    500: '#5A6C7D', // Dark gray from logo
    600: '#485664',
    700: '#36404b',
    800: '#242b32',
    900: '#121519',
  },

  // Gray scale - Modern neutral palette
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Semantic colors
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },

  // Background - Light & airy
  bg: {
    primary: '#ffffff',
    secondary: '#f8fafc',
    tertiary: '#f1f5f9',
    accent: '#e8f4fd',
  },

  // Text
  text: {
    primary: '#0f172a',
    secondary: '#475569',
    tertiary: '#94a3b8',
    inverse: '#ffffff',
  },

  // Borders
  border: {
    primary: '#e2e8f0',
    secondary: '#cbd5e1',
    accent: '#4A90E2',
  },
}

// Dark mode colors - Based on Broker24 logo
export const darkColors = {
  // Brand colors (brighter for dark mode)
  brand: {
    50: '#0f1c2e',
    100: '#1e395b',
    200: '#2d5688',
    300: '#3b73b5',
    400: '#4A90E2',
    500: '#72bbf4', // Brighter for dark mode
    600: '#99cef7',
    700: '#c1e1fa',
    800: '#e8f4fd',
    900: '#f4faff',
  },

  // Secondary colors
  secondary: {
    50: '#06281f',
    100: '#0d513d',
    200: '#13795b',
    300: '#1aa179',
    400: '#20C997',
    500: '#4ddcb9', // Brighter for dark mode
    600: '#80e6cd',
    700: '#b3f0e1',
    800: '#e6faf5',
    900: '#f2fdfb',
  },

  // Accent colors
  accent: {
    50: '#121519',
    100: '#242b32',
    200: '#36404b',
    300: '#485664',
    400: '#5A6C7D',
    500: '#8ea0a9', // Brighter for dark mode
    600: '#a7b5bc',
    700: '#c0cacf',
    800: '#d9dfe2',
    900: '#f2f4f5',
  },

  // Gray scale (for dark mode)
  gray: {
    50: '#0f172a',
    100: '#1e293b',
    200: '#334155',
    300: '#475569',
    400: '#64748b',
    500: '#94a3b8',
    600: '#cbd5e1',
    700: '#e2e8f0',
    800: '#f1f5f9',
    900: '#f8fafc',
  },

  // Semantic colors (same as light)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },

  // Background - Dark & elegant
  bg: {
    primary: '#0f172a',
    secondary: '#1e293b',
    tertiary: '#334155',
    accent: '#1e395b',
  },

  // Text
  text: {
    primary: '#f8fafc',
    secondary: '#cbd5e1',
    tertiary: '#64748b',
    inverse: '#0f172a',
  },

  // Borders
  border: {
    primary: '#334155',
    secondary: '#475569',
    accent: '#72bbf4',
  },
}

// Semantic tokens for color mode switching
export const semanticTokens = {
  colors: {
    'bg.primary': {
      default: lightColors.bg.primary,
      _dark: darkColors.bg.primary,
    },
    'bg.secondary': {
      default: lightColors.bg.secondary,
      _dark: darkColors.bg.secondary,
    },
    'bg.tertiary': {
      default: lightColors.bg.tertiary,
      _dark: darkColors.bg.tertiary,
    },
    'bg.accent': {
      default: lightColors.bg.accent,
      _dark: darkColors.bg.accent,
    },
    'text.primary': {
      default: lightColors.text.primary,
      _dark: darkColors.text.primary,
    },
    'text.secondary': {
      default: lightColors.text.secondary,
      _dark: darkColors.text.secondary,
    },
    'text.tertiary': {
      default: lightColors.text.tertiary,
      _dark: darkColors.text.tertiary,
    },
    'text.inverse': {
      default: lightColors.text.inverse,
      _dark: darkColors.text.inverse,
    },
    'border.primary': {
      default: lightColors.border.primary,
      _dark: darkColors.border.primary,
    },
    'border.secondary': {
      default: lightColors.border.secondary,
      _dark: darkColors.border.secondary,
    },
    'border.accent': {
      default: lightColors.border.accent,
      _dark: darkColors.border.accent,
    },
  },
}
